
import { useEffect, useRef, useState } from "react";

export default function DateMasked({
    id,
    name,
    label,
    value = "",
    onChange,
    onBlur,
    required = false,
    disabled = false,
    error,
    touched,
    helpText,
    min,
    max,
    className = "",
}) {
    const nativeRef = useRef(null); // input type="date"
    const [masked, setMasked] = useState(isoToMasked(value)); // "dd/mm/aaaa" | ""

    // Sincroniza quando o value externo mudar
    useEffect(() => {
        const nextMasked = isoToMasked(value);
        setMasked(nextMasked);
    }, [value]);

    const hasError = touched && (!!error || !masked);

    // Abrir o calendário nativo
    const openPicker = () => {
        if (!nativeRef.current || disabled) return;
        try {
            // showPicker é suportado em Chromium/Android modernos
            if (typeof nativeRef.current.showPicker === "function") {
                nativeRef.current.showPicker();
            } else {
                // fallback razoável
                nativeRef.current.focus();
                nativeRef.current.click();
            }
        } catch {
            nativeRef.current.focus();
            nativeRef.current.click();
        }
    };

    // Quando usuário DIGITA no campo mascarado
    const handleMaskedChange = (e) => {
        const input = e.target.value;
        const maskedNext = toMasked(input); // "dd/mm/aaaa"
        setMasked(maskedNext);

        // Converte para ISO quando completo válido, senão ""
        const iso = maskedToISO(maskedNext);
        emitChange(iso, onChange, required);
    };

    // Quando usuário escolhe no calendário nativo
    const handleNativeChange = (e) => {
        const iso = e.target.value; // "yyyy-MM-dd" | ""
        const maskedFromNative = isoToMasked(iso);
        setMasked(maskedFromNative);
        emitChange(iso, onChange, required);
    };

    return (
        <div className={`flex flex-col gap-1.5 ${className}`}>
            {/* Label do campo */}
            <label
                htmlFor={id}
                className={`text-sm font-medium ${hasError ? "text-destructive" : "text-foreground/80"}`}>
                {label}
                {required && <span className="text-destructive ml-1">*</span>}
            </label>

            {/* Wrapper do input com o botão/ícone */}
            <div className="relative">
                {/* INPUT VISUAL mascarado (type=text) */}
                <input
                    type="text"
                    id={id}
                    name={name}
                    inputMode="numeric"
                    autoComplete="off"
                    value={masked}
                    onChange={handleMaskedChange}
                    onBlur={onBlur}
                    disabled={disabled}
                    required={required}
                    aria-describedby={helpText ? `${id}-helptext` : undefined}
                    className={`
                            p-inputtext
                            w-full
                            h-9
                            px-3
                            pr-10
                            text-sm
                            transition-all
                            duration-150
                            outline-none
                            rounded-md
                            ${disabled && "bg-muted cursor-not-allowed"}
                            ${hasError
                            ? "border border-destructive focus:ring-2 focus:ring-destructive/20"
                            : "border border-input focus:border-primary focus:ring-2 focus:ring-primary/15"
                        }
                    `}
                />

                {/* BOTÃO/ÍCONE que abre o calendário */}
                <button
                    type="button"
                    onClick={openPicker}
                    tabIndex={-1}
                    className={`absolute inset-y-0 right-0 px-3 flex items-center
                        ${disabled ? "cursor-not-allowed text-muted-foreground/50" : "cursor-pointer text-muted-foreground hover:text-foreground"}
                    `}
                    aria-label="Abrir calendário" >
                    <i className={`fa fa-calendar ${hasError && "text-destructive"}`} />
                </button>

                {/* INPUT NATIVO escondido (type=date) para abrir picker e aplicar min/max) */}
                <input
                    ref={nativeRef}
                    type="date"
                    // fica invisível mas presente
                    className="absolute opacity-0 pointer-events-none h-0 w-0"
                    tabIndex={-1}
                    aria-hidden="true"
                    value={value || ""}
                    onChange={handleNativeChange}
                    min={min}
                    max={max}
                    disabled={disabled}
                />
            </div>

            {hasError ? (
                <span className="text-xs text-destructive">{error}</span>
            ) : helpText ? (
                <span className="text-xs text-muted-foreground">{helpText}</span>
            ) : null}

        </div>
    );
}

function isoToMasked(iso) {
    if (!iso || typeof iso !== "string") return "";
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return "";
    const [, yyyy, mm, dd] = m;
    return `${dd}/${mm}/${yyyy}`;
}

function maskedToISO(masked) {
    const m = masked.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!m) return "";
    let [, dd, mm, yyyy] = m;
    // validações simples de faixa
    const day = Number(dd);
    const month = Number(mm);
    const year = Number(yyyy);
    if (month < 1 || month > 12) return "";
    if (day < 1 || day > daysInMonth(month, year)) return "";
    return `${yyyy}-${mm}-${dd}`;
}

function daysInMonth(month, year) {
    return new Date(year, month, 0).getDate();
}

function toMasked(input) {
    const digits = (input || "").replace(/\D/g, "").slice(0, 8); // dd mm yyyy = 8 dígitos
    const len = digits.length;

    if (len <= 2) return digits;
    if (len <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

function emitChange(iso, onChange) {
    if (typeof onChange !== "function") return;
    onChange(iso);
}
